import React, { useState, useEffect } from 'react'
import { Form, Button, Container, Row, Col } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useVerifyOtpMutation, useSendOtpMutation } from '../slices/usersApiSlice'
import { parseUserInfo } from '../utils/parseUtil'
import { FaEye, FaEyeSlash } from 'react-icons/fa'

const OtpScreen = () => {
    const location = useLocation()
    const { userName } = location.state || {}
    const [otp, setOtp] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showPasswordFields, setShowPasswordFields] = useState(false)
    const [passwordVisible, setPasswordVisible] = useState(false)
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false)
    const [timer, setTimer] = useState(60)
    const [showResendButton, setShowResendButton] = useState(false)
    const [otpVerified, setOtpVerified] = useState(false)  // New state to track OTP verification
    const [verifyOtp, { isLoading }] = useVerifyOtpMutation()
    const [sendOtp, { isLoading: sendOtpLoading }] = useSendOtpMutation()
    const navigate = useNavigate()



    // Fetch userId and companyCode from localStorage
    const userId = localStorage.getItem('userId')
    const companyCode = localStorage.getItem('companyCode')

    useEffect(() => {
        if (!userId || !companyCode) {
            console.error('User ID or Company Code is missing')
            console.error('Missing user information. Please try again.')
            return
        }

        const startTime = localStorage.getItem('otpTimerStartTime')
        if (startTime) {
            const elapsedTime = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000)
            const remainingTime = 60 - elapsedTime
            if (remainingTime > 0) {
                setTimer(remainingTime)
            } else {
                setTimer(0)
                setShowResendButton(true)
            }
        } else {
            localStorage.setItem('otpTimerStartTime', new Date().toISOString())
        }
    }, [userId, companyCode])

    useEffect(() => {
        let interval
        if (timer > 0) {
            setShowResendButton(false)
            interval = setInterval(() => {
                setTimer(prevTimer => prevTimer - 1)
            }, 1000)
        } else {
            clearInterval(interval)
            localStorage.removeItem('otpTimerStartTime')
            setShowResendButton(true)
        }
        return () => clearInterval(interval)
    }, [timer])



    const [verificationCode, setVerificationCode] = useState(null)




    const handleResendOtp = async () => {
        if (!userId || !companyCode) {
            toast.error('User ID or Company Code is missing')
            return
        }

        try {
            const SMSTYPE = process.env.REACT_APP_SMS_TYPE || ""
            const otpFormData = new FormData()
            otpFormData.append('title', 'SentVerificationCodeStaff')
            otpFormData.append('description', 'SentVerificationCodeStaff')
            otpFormData.append('ReqUserID', userId)
            otpFormData.append('ReqCompanyCode', companyCode)
            otpFormData.append('ReqSMSType', SMSTYPE)

            const res = await sendOtp(otpFormData) // Capture response
            const parsedUserInfo = parseUserInfo(res)
            const code = parsedUserInfo[0]?.InfoField2 // Extract 'Ver. Code' value
            setVerificationCode(code)
            console.log('Resend OTP Response:', parsedUserInfo)

            toast.success('OTP resent successfully')
            setTimer(60)
            localStorage.setItem('otpTimerStartTime', new Date().toISOString())
            setShowResendButton(false)  // Hide resend button after successful resend
        } catch (err) {
            console.error('Resend OTP Error:', err)
            toast.error('Failed to resend OTP')
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!userId || !companyCode) {
            toast.error('User ID or Company Code is missing')
            return
        }

        const SMSTYPE = process.env.REACT_APP_SMS_TYPE || ""
        const formData = new FormData()
        formData.append('ReqVeriCode', otp)
        formData.append('ReqUserID', userId)
        formData.append('ReqCompanyCode', companyCode)
        formData.append('description', 'DoVerificationStaff')
        formData.append('title', 'DoVerificationStaff')
        formData.append('ReqSMSType', SMSTYPE)


        for (let [key, value] of formData.entries()) {
            console.log(`${key}: ${value}`)
        }

        if (showPasswordFields) {
            if (password !== confirmPassword) {
                toast.error('Passwords do not match')
                return
            }
            formData.append('ReqNewPass', password)
        }

        try {
            const res = await verifyOtp(formData)
            const parsedUserInfo = parseUserInfo(res)

            if (parsedUserInfo) {
                const infoField1 = parsedUserInfo[0]?.InfoField1
                const infoField3 = parsedUserInfo[0]?.InfoField3

                if (showPasswordFields) {
                    if (infoField1 === "0" && infoField3) {
                        toast.success('Password updated successfully')
                        setOtpVerified(true)  // Set OTP verification status
                        localStorage.removeItem('userId')
                        localStorage.removeItem('companyCode')
                        localStorage.removeItem('userName')
                        setTimeout(() => {
                            navigate('/login')
                        }, 500)
                    } else {
                        toast.error('Creating Password Failed')
                    }
                } else {
                    if (infoField1 === "0") {
                        setShowPasswordFields(true)
                    } else if (infoField1 === "1") {
                        toast.success('OTP verified successfully')
                        setOtpVerified(true)  // Set OTP verification status
                        localStorage.removeItem('userId')
                        localStorage.removeItem('companyCode')
                        localStorage.removeItem('userName')
                        setTimeout(() => {
                            navigate('/login')
                        }, 500)
                    } else {
                        toast.error(parsedUserInfo[0]?.InfoField || 'Invalid OTP')
                    }
                }
            } else {
                toast.error('Failed to parse user info')
            }
        } catch (err) {
            console.error('Verification Error:', err)
            toast.error(err?.data?.message || err.error)
        }
    }

    return (
        <Container className="form-container">
            <Row className="justify-content-md-center">
                <Col md={6}>
                    <h3 className="mt-4 text-center form-title">Welcome Back, {userName}</h3>
                    <Form className="border p-4 rounded form-background" onSubmit={handleSubmit}>
                        <Form.Group controlId="otp" className="mb-3">
                            <Form.Label>OTP </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                required
                                className="form-control-hover"
                                disabled={showPasswordFields}
                            />
                        </Form.Group>
                        {showPasswordFields && (
                            <>
                                <Form.Group controlId="password" className="mb-3">
                                    <Form.Label>New Password</Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type={passwordVisible ? 'text' : 'password'}
                                            placeholder="Enter New Password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="form-control-hover"
                                        />
                                        <span
                                            className="position-absolute top-50 end-0 translate-middle-y me-2"
                                            onClick={() => setPasswordVisible(!passwordVisible)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </div>
                                </Form.Group>
                                <Form.Group controlId="confirmPassword" className="mb-3">
                                    <Form.Label>Confirm New Password</Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type={confirmPasswordVisible ? 'text' : 'password'}
                                            placeholder="Confirm New Password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            className="form-control-hover"
                                        />
                                        <span
                                            className="position-absolute top-50 end-0 translate-middle-y me-2"
                                            onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {confirmPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </div>
                                </Form.Group>
                            </>
                        )}
                        <Button
                            variant="primary"
                            type="submit"
                            className="mt-3 btn-custom"
                            disabled={isLoading || !otp || (showPasswordFields && (!password || !confirmPassword))}
                        >
                            {showPasswordFields ? 'Submit' : 'Verify OTP'}
                        </Button>
                        {!showPasswordFields && timer > 0 && (
                            <div className="mt-3">
                                <span>Resend OTP in {timer} seconds</span>
                            </div>
                        )}
                        {!showPasswordFields && showResendButton && !otpVerified && (
                            <Button
                                variant="secondary"
                                className="mt-3 btn-custom"
                                onClick={handleResendOtp}
                                disabled={sendOtpLoading}
                            >
                                Resend OTP
                            </Button>
                        )}
                    </Form>
                    {verificationCode ? (
                        <h5>OTP: {verificationCode}</h5>
                    ) : (
                        <p>Resend OTP To Get Code</p>
                    )}
                </Col>
            </Row>
        </Container>
    )
}

export default OtpScreen
